import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { OR_LOGO, OR_ARENA } from "./OpenRing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  legal_name: "", ring_name: "", email: "", phone: "", city_state: "",
  bio: "", years_active: "", trainer: "", resume: "",
  facebook: "", instagram: "", x: "", tiktok: "", other: "",
  match_url: "", promo_url: "", consent: false, website: "",
};

const SECTIONS = [
  { title: "Personal & Contact Information", fields: [
    { k: "legal_name", label: "Full Legal Name", req: true, auto: "name" },
    { k: "ring_name", label: "Wrestling / Ring Name", req: true },
    { k: "email", label: "Email Address", req: true, type: "email", auto: "email" },
    { k: "phone", label: "Phone Number", req: true, type: "tel", auto: "tel" },
    { k: "city_state", label: "City and State", req: true, ph: "Tyler, TX" },
  ] },
  { title: "Wrestling Background", fields: [
    { k: "bio", label: "Professional Wrestling Bio", multi: true },
    { k: "years_active", label: "Years Active in Professional Wrestling", req: true, ph: "e.g. 5" },
    { k: "trainer", label: "Trainer / Wrestling School", req: true },
    { k: "resume", label: "Wrestling Résumé", multi: true, ph: "Promotions worked, titles held, notable matches..." },
  ] },
  { title: "Social Media", note: "Optional — leave blank if not applicable", fields: [
    { k: "facebook", label: "Facebook URL", url: true },
    { k: "instagram", label: "Instagram URL", url: true },
    { k: "x", label: "X / Twitter URL", url: true },
    { k: "tiktok", label: "TikTok URL", url: true },
    { k: "other", label: "Other Website / Social Media URL", url: true },
  ] },
  { title: "Wrestling Footage", note: "Optional — YouTube, Vimeo, Facebook, or similar", fields: [
    { k: "match_url", label: "Link to a Recent Wrestling Match", url: true },
    { k: "promo_url", label: "Link to a Recent Wrestling Promo", url: true },
  ] },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (f) => {
  const e = {};
  SECTIONS.forEach((s) => s.fields.forEach((d) => {
    const v = f[d.k].trim();
    if (d.req && !v) e[d.k] = "Required";
  }));
  if (f.email.trim() && !EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email";
  if (!f.consent) e.consent = "Please check this box to continue";
  return e;
};

const inputCls = (err) =>
  `w-full bg-[#0c0c0c] border ${err ? "border-red-500" : "border-white/15"} focus:border-brand focus:outline-none text-white px-4 py-3 text-base transition-colors placeholder:text-zinc-600`;

const Field = ({ d, value, error, onChange }) => (
  <label className={`block ${d.multi ? "md:col-span-2" : ""}`}>
    <span className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {d.label} {d.req && <span className="text-brand">*</span>}
    </span>
    {d.multi ? (
      <textarea data-testid={`apply-${d.k}-input`} rows={5} value={value} placeholder={d.ph} onChange={(e) => onChange(d.k, e.target.value)} className={inputCls(error)} />
    ) : (
      <input data-testid={`apply-${d.k}-input`} type={d.type || "text"} inputMode={d.url ? "url" : undefined} autoComplete={d.auto} value={value}
        placeholder={d.ph || (d.url ? "Optional" : "")} onChange={(e) => onChange(d.k, e.target.value)} className={inputCls(error)} />
    )}
    {error && <span data-testid={`apply-${d.k}-error`} className="block text-red-400 text-sm mt-1">{error}</span>}
  </label>
);

const Confirmation = () => (
  <div data-testid="apply-confirmation" className="max-w-2xl mx-auto text-center py-10">
    <CheckCircle2 className="w-16 h-16 text-brand mx-auto mb-6" />
    <h2 className="font-display text-5xl md:text-6xl uppercase leading-none mb-8">Application Received</h2>
    <div className="space-y-4 text-zinc-300 text-base md:text-lg">
      <p className="text-white font-semibold">Thank you for your interest in CWF OPEN RING!</p>
      <p>Your application has been submitted for consideration.</p>
      <p>CWF management will review your information and contact selected applicants regarding potential opportunities.</p>
    </div>
    <p className="font-display text-3xl md:text-4xl tracking-[0.12em] mt-10">THE RING IS <span className="text-brand">YOURS.</span></p>
    <Link to="/openring" data-testid="apply-back-link" className="inline-block mt-10 font-display text-xl uppercase text-brand hover:text-brand-hi">Back to Open Ring</Link>
  </div>
);

const Header = () => (
  <section className="relative pt-32 pb-12 px-6 md:px-12 lg:px-20 overflow-hidden">
    <img src={OR_ARENA} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-[#050505]" />
    <div className="relative z-10 max-w-4xl mx-auto">
      <Link to="/openring" data-testid="apply-logo-link"><img src={OR_LOGO} alt="OPEN RING" className="h-12 md:h-16 w-auto" /></Link>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-none mt-8">Talent Application</h1>
      <p className="text-zinc-400 mt-4 text-base">Fields marked <span className="text-brand">*</span> are required.</p>
    </div>
  </section>
);

export default function OpenRingApply() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");
  const startedAt = useRef(Date.now());

  const onChange = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (sending) return;
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted fields.");
      document.querySelector(`[data-testid="apply-${Object.keys(e)[0]}-input"]`)?.focus();
      return;
    }
    setSending(true);
    setServerError("");
    try {
      await axios.post(`${API}/openring/apply`, { ...form, started_at: startedAt.current });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setServerError(typeof d === "string" ? d : "We couldn't submit your application. Please check your information and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="openring-apply-page">
      <Header />
      <section className="px-6 md:px-12 lg:px-20 pb-24">
        {done ? <Confirmation /> : (
          <form data-testid="openring-apply-form" onSubmit={submit} noValidate className="max-w-4xl mx-auto space-y-14">
            {SECTIONS.map((s) => (
              <fieldset key={s.title}>
                <legend className="flex items-baseline gap-3 mb-6 border-b-2 border-brand pb-2 w-full">
                  <span className="font-display text-3xl md:text-4xl uppercase">{s.title}</span>
                  {s.note && <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{s.note}</span>}
                </legend>
                <div className="grid md:grid-cols-2 gap-6">
                  {s.fields.map((d) => <Field key={d.k} d={d} value={form[d.k]} error={errors[d.k]} onChange={onChange} />)}
                </div>
              </fieldset>
            ))}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => onChange("website", e.target.value)}
              className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
            <div className="border border-white/10 bg-[#0c0c0c] p-6 md:p-8 space-y-6">
              <p data-testid="apply-disclaimer" className="text-zinc-300 text-sm md:text-base leading-relaxed">
                "OPEN RING is intended for trained professional wrestlers. All submissions are reviewed by CWF management. Submitting an application does not guarantee selection or booking. Selected applicants will be contacted directly regarding potential opportunities."
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input data-testid="apply-consent-checkbox" type="checkbox" checked={form.consent} onChange={(e) => onChange("consent", e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#FACC15] shrink-0" />
                <span className="text-white text-sm md:text-base">
                  I understand that submitting an application does not guarantee a booking, and I consent to CWF reviewing my information for booking consideration. <span className="text-brand">*</span>
                </span>
              </label>
              {errors.consent && <p data-testid="apply-consent-error" className="text-red-400 text-sm">{errors.consent}</p>}
              {serverError && <p data-testid="apply-server-error" className="text-red-400 text-sm md:text-base border border-red-500/40 bg-red-500/10 px-4 py-3">{serverError}</p>}
              <button data-testid="apply-submit-btn" type="submit" disabled={sending}
                className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-hi disabled:opacity-60 disabled:cursor-not-allowed text-black font-display text-2xl md:text-3xl uppercase tracking-wide px-10 py-4 transition-colors">
                {sending && <Loader2 className="w-6 h-6 animate-spin" />}
                {sending ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
